import useSWRInfinite from "swr/infinite";

import { extractAPIRequestErrorMessage } from "utils";

import { useAPI } from "./useAPI";
import { DEFAULT_SWR_OPTIONS } from "./utils";

import type { AxiosError } from "axios";
import type {
  SWRInfiniteConfiguration,
  SWRInfiniteKeyLoader,
} from "swr/infinite";

/*
Paged counterpart to `useSwrFetch`, for lists a user appends to by scrolling.
The accumulated page list is derived from the SWR cache rather than held in
component state, which keeps a re-issued query from double-appending and stops a
stale offset being read off a page the caller is no longer showing.
*/

const PAGED_SWR_OPTIONS: SWRInfiniteConfiguration = {
  ...DEFAULT_SWR_OPTIONS,
  // A page already fetched does not change while the user scrolls, so only the
  // newly appended page is fetched on each `setSize`.
  revalidateAll: false,
  revalidateFirstPage: false,
};

interface UseSwrInfiniteFetchProps<T> {
  /*
  Returns the URL for a page given its index and the page before it, or null to
  stop paging. See https://swr.vercel.app/docs/pagination#useswrinfinite.
  */
  getKey: SWRInfiniteKeyLoader<T>;
  errorMessage: string;
  errorMessageKey?: string;
  swrOptions?: SWRInfiniteConfiguration<T, AxiosError>;
}

interface UseSwrInfiniteFetchResult<T> {
  pages: T[];
  /** Pages requested so far, which exceeds `pages.length` while one is loading. */
  size: number;
  setSize: (size: number | ((size: number) => number)) => void;
  loading: boolean;
  validating: boolean;
  error: string | null;
}

export function useSwrInfiniteFetch<T>(
  props: UseSwrInfiniteFetchProps<T>,
): UseSwrInfiniteFetchResult<T> {
  const { getKey, errorMessage, errorMessageKey, swrOptions } = props;

  const { api } = useAPI();

  const {
    data,
    error: swrError,
    isLoading,
    isValidating,
    size,
    setSize,
  } = useSWRInfinite<T, AxiosError>(
    getKey,
    async (url: string) => {
      const response = await api<T>("get", url);
      return response.data;
    },
    { ...PAGED_SWR_OPTIONS, ...swrOptions },
  );

  return {
    pages: data ?? [],
    size,
    setSize,
    loading: isLoading,
    validating: isValidating,
    error: swrError
      ? extractAPIRequestErrorMessage(swrError, errorMessage, errorMessageKey)
      : null,
  };
}
